/**
 * Shared error model for the list wizards (Add / Edit).
 *
 * Discriminated union; one variant per scenario a wizard must handle. The
 * `network` / `api` transport failures come from the app-wide
 * `@/utils/infra-error` classifier (shared with every other feature, e.g.
 * Licences); this module adds the wizard-specific `validation` (field-level
 * messages) and `cancelled` (a save was superseded/aborted) variants and pins
 * the save-specific default message. Pure and framework-free so both features
 * can reuse it without depending on each other.
 */
import { classifyInfraError, type InfraFailure } from '@/utils/infra-error';

export type ListWizardError =
  | InfraFailure
  | { kind: 'validation'; fields: Record<string, string> }
  | { kind: 'cancelled' };

/**
 * Map an infrastructure error into a domain `ListWizardError`. Only ever yields
 * a `network` / `api` failure; `validation` errors are produced directly by
 * the feature's `validateDraft`, and `cancelled` by the saga's race.
 */
export function toListWizardError(e: unknown): ListWizardError {
  return classifyInfraError(e, 'Failed to save list.');
}
